/**
 * Mobile History Page
 *
 * Vertical list view of previous blog posts with:
 * - Search/filter bar at top
 * - List items with status badges
 * - Swipe actions (delete, copy)
 * - Pull to refresh
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, type PanInfo } from 'framer-motion';
import {
  Search,
  Clock,
  FileText,
  Trash2,
  Copy,
  ExternalLink,
  RefreshCw,
  Filter,
} from 'lucide-react';

interface HistoryItem {
  id: string;
  title: string;
  topic: string;
  date: string;
  status: 'draft' | 'published' | 'processing' | 'failed';
  wordCount?: number;
  previewUrl?: string;
}

interface MobileHistoryPageProps {
  getAuthHeaders?: () => Promise<Record<string, string>>;
}

const API_URL = import.meta.env.VITE_API_URL || 'https://claude-essay-agent-production.up.railway.app';

export const MobileHistoryPage: React.FC<MobileHistoryPageProps> = ({
  getAuthHeaders,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [swipedItemId, setSwipedItemId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [showFilterMenu, setShowFilterMenu] = useState(false);

  const fetchHistory = async () => {
    setIsLoading(true);
    try {
      const headers = getAuthHeaders ? await getAuthHeaders() : {};
      const response = await fetch(`${API_URL}/api/v1/blog/history?limit=50`, { headers });
      if (response.ok) {
        const data = await response.json();
        const items: HistoryItem[] = data.map((item: { id: string; title: string; excerpt?: string; word_count?: number; created_at: string; status: string }) => ({
          id: item.id,
          title: item.title || 'Untitled',
          topic: item.excerpt || '',
          date: new Date(item.created_at).toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' }),
          status: item.status === 'completed' ? 'published' as const : item.status === 'processing' ? 'processing' as const : item.status === 'failed' ? 'failed' as const : 'draft' as const,
          wordCount: item.word_count || undefined,
          previewUrl: undefined,
        }));
        setHistory(items);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const handleRefresh = async () => {
    await fetchHistory();
  };

  const handleDelete = (id: string) => {
    setHistory(history.filter((item) => item.id !== id));
    setSwipedItemId(null);
  };

  const handleCopy = async (item: HistoryItem) => {
    try {
      await navigator.clipboard.writeText(item.title);
      // Show toast
    } catch (error) {
      console.error('Failed to copy:', error);
    }
    setSwipedItemId(null);
  };

  const filteredHistory = history.filter((item) => {
    const matchesSearch =
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.topic.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter =
      filter === 'all' || item.status === filter;
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status: HistoryItem['status']) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-700';
      case 'processing':
        return 'bg-amber-100 text-amber-700';
      case 'failed':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="pb-24">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-gray-50 px-4 pt-4 pb-3 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-2xl font-bold text-gray-900">History</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="p-2 rounded-lg hover:bg-gray-100 transition touch-target"
            >
              <RefreshCw
                className={`w-5 h-5 text-gray-600 ${isLoading ? 'animate-spin' : ''}`}
              />
            </button>
            <div className="relative">
              <button
                onClick={() => setShowFilterMenu(!showFilterMenu)}
                className="p-2 rounded-lg hover:bg-gray-100 transition touch-target"
              >
                <Filter className="w-5 h-5 text-gray-600" />
              </button>

              <AnimatePresence>
                {showFilterMenu && (
                  <>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-40"
                      onClick={() => setShowFilterMenu(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className="absolute right-0 top-12 w-40 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50"
                    >
                      {(['all', 'published', 'draft'] as const).map((f) => (
                        <button
                          key={f}
                          onClick={() => {
                            setFilter(f);
                            setShowFilterMenu(false);
                          }}
                          className={`w-full px-4 py-2.5 text-left text-sm ${
                            filter === f
                              ? 'bg-purple-50 text-purple-700 font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {f.charAt(0).toUpperCase() + f.slice(1)}
                        </button>
                      ))}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search posts..."
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-base bg-white"
          />
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        {isLoading && history.length === 0 ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-4 border border-gray-100 animate-pulse"
              >
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : filteredHistory.length > 0 ? (
          <div className="space-y-3">
            {filteredHistory.map((item) => (
              <SwipeableHistoryItem
                key={item.id}
                item={item}
                isOpen={swipedItemId === item.id}
                onSwipe={(open) => setSwipedItemId(open ? item.id : null)}
                onDelete={() => handleDelete(item.id)}
                onCopy={() => handleCopy(item)}
                statusColor={getStatusColor(item.status)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {searchQuery ? 'No posts found' : 'No posts yet'}
            </h3>
            <p className="text-gray-500 text-sm">
              {searchQuery
                ? 'Try a different search term'
                : 'Create your first blog post to get started'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Swipeable History Item Component
interface SwipeableHistoryItemProps {
  item: HistoryItem;
  isOpen: boolean;
  onSwipe: (open: boolean) => void;
  onDelete: () => void;
  onCopy: () => void;
  statusColor: string;
}

const SwipeableHistoryItem: React.FC<SwipeableHistoryItemProps> = ({
  item,
  isOpen,
  onSwipe,
  onDelete,
  onCopy,
  statusColor,
}) => {
  const constraintsRef = useRef(null);

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.x < -80) {
      onSwipe(true);
    } else {
      onSwipe(false);
    }
  };

  return (
    <div className="relative overflow-hidden rounded-xl" ref={constraintsRef}>
      {/* Swipe Actions Background */}
      <div className="absolute inset-y-0 right-0 flex items-center">
        <button
          onClick={onCopy}
          className="h-full px-4 bg-blue-500 text-white flex items-center justify-center"
        >
          <Copy className="w-5 h-5" />
        </button>
        <button
          onClick={onDelete}
          className="h-full px-4 bg-red-500 text-white flex items-center justify-center"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      {/* Main Content */}
      <motion.div
        drag="x"
        dragConstraints={{ left: -120, right: 0 }}
        dragElastic={0.1}
        onDragEnd={handleDragEnd}
        animate={{ x: isOpen ? -120 : 0 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className="bg-white p-4 border border-gray-100 rounded-xl relative z-10"
      >
        <div className="flex items-start gap-3">
          <div className="p-2 bg-gray-100 rounded-lg flex-shrink-0">
            <FileText className="w-5 h-5 text-gray-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 truncate">{item.title}</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <div className="flex items-center gap-1 text-gray-500">
                <Clock className="w-3 h-3" />
                <span className="text-xs">{item.date}</span>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor}`}>
                {item.status}
              </span>
              {item.wordCount && (
                <span className="text-xs text-gray-400">
                  {item.wordCount.toLocaleString()} words
                </span>
              )}
            </div>
          </div>
          {item.previewUrl && (
            <a
              href={item.previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 hover:bg-gray-100 rounded-lg touch-target"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="w-4 h-4 text-gray-400" />
            </a>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default MobileHistoryPage;
